import React, { useState, useEffect } from "react";
import { Link as RouterLink, NavLink } from "react-router-dom";
import {
  Container,
  Chip,
  Button,
  Typography,
  Backdrop,
  Snackbar,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { AppConfig, roles } from "../../../../configs/admin-config";
import DoneIcon from "@mui/icons-material/Done";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useHistory } from "react-router-dom";
import MuiAlert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useHttp from "../../../../utils/http";
import ConfirmationDialog from "../../Dialog/ConfirmationDialog";
import { getAccessToken, checkIfRoleExists } from "../../../../utils/oauth";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  topBar: {
    marginTop: 88,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginLeft: 45,
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  pageTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  rightActions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginRight: 60,
    paddingTop: theme.spacing(0.5),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    gap: theme.spacing(2),
  },
  emptyIconWrapper: {
    width: 72,
    height: 72,
    borderRadius: 16,
    backgroundColor: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyIcon: {
    fontSize: 36,
    color: "#64748b",
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.text.primary,
    margin: 0,
  },
  emptySubtitle: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    margin: 0,
  },
  button: {
    textTransform: "none",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const PendingPage = (props) => {
  const history = useHistory();
  const { isLoading, data, error, sendRequest, reqExtra, isOpen } = useHttp();
  const { classes } = useStyles();
  const [copySnack, setCopy] = useState(false);
  const [backdropOpen, setbackDrop] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [failSnack, setFail] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const [tradeReferenceEnabled, setTradeReferenceEnabled] = useState(
    props.location.state?.item?.isTradeReferenceEnabled
  );

  useEffect(() => {
    if (!checkIfRoleExists(roles.finance) || !props.location.state) {
      history.replace({
        pathname: AppConfig.urls.NO_ACCESS_PAGE,
      });
    }
    getResellerData();
  }, []);

  const handleChangeExpiry = (shouldExpire) => () => {
    handleConfirmationDialog({
      title: "You are about to make a change to a reseller!",
      text: "Are you sure you want to " + (shouldExpire ? "enable expiry" : "disable expiry") + "?",
      confirmAction: () => {
        changeExpiry(shouldExpire);
      },
    });
  };

  const getResellerData = () => {
    if (props.match.params.id) {
      sendRequest(
        AppConfig.baseUrl + AppConfig.partnerData + props.match.params.id,
        "GET",
        null,
        AppConfig.partnerData
      );
    }
  };

  const changeExpiry = (shouldExpire) => {
    if (props.match.params.id) {
      var payload = {
        linkId: props.match.params.id.toString(),
        shouldExpire: shouldExpire,
      };
      sendRequest(
        AppConfig.baseUrl + AppConfig.changeResellerExpiry,
        "POST",
        JSON.stringify(payload),
        AppConfig.changeResellerExpiry
      );
    }
  };

  const handleConfirmationDialog = (content) => {
    setConfirmationDialog(content);
  };

  const handleChangeStatus = (status) => () => {
    handleConfirmationDialog({
      title: "You are about to make a change to a reseller!",
      text: "Are you sure you want to " + (status === "active" ? "activate" : "deactivate") + " this reseller?",
      confirmAction: () => {
        changeStatus(status);
      },
    });
  };

  const changeStatus = async (status) => {
    setbackDrop(true);

    const requestURL = AppConfig.baseUrl + AppConfig.partnerData + props.match.params.id + "/" + AppConfig.linkStatus;

    let data = {
      status: status,
      linkId: parseInt(props.match.params.id),
    };
    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify(data),
      });
      let body = await response.json();
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        props.history.goBack();
      } else {
        console.error(body.message);
        setFail(true);
      }
      return null;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  const handleClose = (reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setFail(false);
    setCopy(false);
  };

  const handleGoBack = () => {
    history.push(AppConfig.urls.LANDING_PAGE);
  };

  useEffect(() => {
    switch (reqExtra) {
      case AppConfig.getResellerData:
        if (data) {
          if (data && !error) {
            if (data.companyName) {
              setCompanyData(data.companyName);
            }
            if (data.link) {
              setLinkData(data.linkId);
            }
          } else {
            console.warn("Not retrieved any data from backend");
          }
        } else if (error) {
          console.warn("Not retrieved any data from backend");
        }
        setbackDrop(false);
        break;
      case AppConfig.changeResellerExpiry:
        if (data && !error) {
          getResellerData();
        } else if (error) {
          console.warn("Not retrieved any data from backend");
        }
        setbackDrop(false);
        break;
      default:
        break;
    }
  }, [data, reqExtra, isOpen, isLoading, error]);

  const handleTradeReference = (value) => {
    handleConfirmationDialog({
      title: "You are about to make a change to a reseller!",
      text: "Are you sure you want to " + (value ? "enable" : "disable") + " Trade Reference for this reseller?",
      confirmAction: () => {
        changeValue(value);
      },
    });
  };

  const changeValue = async (value) => {
    setbackDrop(true);
    const isTradeReferenceEnabled = value
    const requestURL =
      AppConfig.baseUrl +
      AppConfig.partnerData +
      props.match.params.id;

    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify({ isTradeReferenceEnabled }),
      });
      let body = await response.json();
      setbackDrop(false);
      if (response.status === 200) {
        setSuccess(true);
        setTradeReferenceEnabled(value);
        history.replace({
          ...props.location,
          state: {
            ...props.location.state,
            item: {
              ...props.location.state?.item,
              isTradeReferenceEnabled: value,
            },
          },
        });
      } else {
        console.error(body.message);
        setFail(true);
      }
      return null;
    } catch (error) {
      console.error(error);
      return error;
    }
  };

  return (
    <>
      <Container maxWidth={false} style={{ paddingLeft: 16, paddingRight: 16 }}>
        <Backdrop open={backdropOpen} className={classes.backdrop}>
          <CircularProgress color="secondary" />
        </Backdrop>

        <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={successSnack} autoHideDuration={3000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="success">
            Success!
          </Alert>
        </Snackbar>
        <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={copySnack} autoHideDuration={3000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="success">
            Copied to Clipboard!
          </Alert>
        </Snackbar>
        <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={failSnack} autoHideDuration={3000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error">
            Server Error!
          </Alert>
        </Snackbar>
        <div className={classes.topBar}>
          <div className={classes.leftColumn}>
            <div className={classes.leftSection}>
              <NavLink to={AppConfig.urls.ADMIN + AppConfig.urls.LANDING_PAGE}>
                <IconButton aria-label="back">
                  <ArrowBackIcon />
                </IconButton>
              </NavLink>
              <Typography className={classes.pageTitle}>
                Return to Dashboard
              </Typography>
            </div>
            <Chip
              variant="outlined"
              label={"Form Status: Pending " + (linkData?.isExpired ? "(Expired)" : "")}
              color={"primary"}
              style={{ marginLeft: 12 }}
            />
          </div>

          <div className={classes.rightActions}>
            {linkData?.isExpiring !== undefined ? (
              Boolean(linkData?.isExpiring) ? (
                <Button
                  className={classes.button}
                  variant="outlined"
                  color="primary"
                  onClick={handleChangeExpiry(false)}
                >
                  Disable Expiry
                </Button>
              ) : (
                <Button
                  className={classes.button}
                  variant="outlined"
                  color="primary"
                  onClick={handleChangeExpiry(true)}
                >
                  Enable Expiry
                </Button>
              )
            ) : null}

            {checkIfRoleExists(roles.admin) && (
              <>
                {props.location.state?.item?.status === "active" && (
                  <Button
                    className={classes.button}
                    variant="outlined"
                    color="primary"
                    onClick={handleChangeStatus("inactive")}
                  >
                    Deactivate
                  </Button>
                )}
                {props.location.state?.item?.status === "inactive" && (
                  <Button
                    className={classes.button}
                    variant="outlined"
                    color="primary"
                    onClick={handleChangeStatus("active")}
                  >
                    Activate
                  </Button>
                )}
              </>
            )}

            {(checkIfRoleExists(roles.finance) || checkIfRoleExists(roles.legal) || checkIfRoleExists(roles.admin)) && (
              tradeReferenceEnabled ? (
                <Button className={classes.button} variant="outlined" color="primary" onClick={() => handleTradeReference(false)}>
                  Disable TR
                </Button>
              ) : (
                <Button className={classes.button} variant="outlined" color="primary" onClick={() => handleTradeReference(true)}>
                  Enable TR
                </Button>
              )
            )}

            {checkIfRoleExists(roles.admin) && (
              <Button className={classes.button} variant="outlined" color="primary" startIcon={<DoneIcon />} disabled>
                Editing Allowed
              </Button>
            )}
          </div>
        </div>

        {/* Empty state */}
        <div className={classes.emptyState}>
          <div className={classes.emptyIconWrapper}>
            <DescriptionOutlinedIcon className={classes.emptyIcon} />
          </div>
          <Typography className={classes.emptyTitle}>Form not submitted yet</Typography>
          <Typography className={classes.emptySubtitle}>
            {props.location.state?.item?.companyName} has not filled out the form yet
          </Typography>
        </div>
      </Container>
      {confirmationDialog && <ConfirmationDialog content={confirmationDialog} handleOpen={handleConfirmationDialog} />}
    </>
  );
};

export default PendingPage;
